/**
 * Project format presets, shared by "New Project" (import.ts) and the
 * Duplicate dialog (DEV-1091, where a copy is made for another format).
 *
 * `width`/`height` are the project's coordinate space (`origCoordsWidth` /
 * `origCoordsHeight`); `dims` are the resolution levels written to
 * `dimensionsMap`. Extracted from import.ts when the second caller appeared —
 * two hardcoded copies of these numbers would be two places to disagree about
 * what 9:16 means.
 */

export interface AspectPreset {
	width: number;
	height: number;
	dims: Record<string, { width: number; height: number }>;
}

export const ASPECT_PRESETS: Record<string, AspectPreset> = {
	'16:9': {
		width: 1920, height: 1080,
		dims: { low: { width: 640, height: 360 }, medium: { width: 1280, height: 720 } },
	},
	'9:16': {
		width: 1080, height: 1920,
		dims: { low: { width: 360, height: 640 }, medium: { width: 720, height: 1280 } },
	},
	'1:1': {
		width: 1080, height: 1080,
		dims: { low: { width: 360, height: 360 }, medium: { width: 720, height: 720 } },
	},
};

export const ASPECT_OPTIONS: { label: string; value: string }[] = [
	{ label: '16:9 (Landscape)', value: '16:9' },
	{ label: '9:16 (Portrait)', value: '9:16' },
	{ label: '1:1 (Square)', value: '1:1' },
	{ label: 'Custom', value: 'custom' },
];

/**
 * Resolution levels for a size with no matching preset. Mirrors how the
 * presets above were built (a third and two thirds of full size) rather than
 * falling back to a hardcoded 16:9 shape, which would describe the wrong
 * aspect for every custom size.
 */
export function deriveDimensionsMap(width: number, height: number): Record<string, { width: number; height: number }> {
	return {
		low: { width: Math.round(width / 3), height: Math.round(height / 3) },
		medium: { width: Math.round(width * 2 / 3), height: Math.round(height * 2 / 3) },
	};
}

/**
 * Which preset (if any) a given size is. Used to preselect the format of a
 * project being duplicated — matched on the exact dimensions rather than the
 * ratio, so a 1280×720 project reads as Custom and keeps its own numbers
 * instead of being silently promoted to 1920×1080.
 */
export function matchAspectPreset(width: number, height: number): string {
	for (const [key, preset] of Object.entries(ASPECT_PRESETS)) {
		if (preset.width === width && preset.height === height) return key;
	}
	return 'custom';
}
